interface TextColors {
    emphasized: string;
    secondary: string;
}

interface TooltipConfig {
    backgroundColor: string;
    bodyColor: string;
    bodyFont: {
        size: number;
    };
    borderColor: string;
    borderWidth: number;
    cornerRadius: number;
    padding: number;
    titleColor: string;
    titleFont: {
        size: number;
    };
}

/**
 * Creates a consistent tooltip configuration for Chart.js charts
 * throughout the application
 *
 * This provides a unified design system for all chart tooltips with:
 * - Theme-aware background colors
 * - Consistent border styling
 * - Standardized font sizes and padding
 * - Smooth rounded corners
 *
 * @param isDark - Whether dark theme is active
 * @param textColors - Text color configuration with emphasized and secondary colors
 * @returns Chart.js tooltip configuration object that can be spread into plugins.tooltip
 *
 * @example
 * ```typescript
 * const options = {
 *   plugins: {
 *     tooltip: {
 *       ...createTooltipConfig(isDark, textColors),
 *       callbacks: {
 *         label: (context) => `Custom: ${context.parsed.y}`
 *       }
 *     }
 *   }
 * }
 * ```
 */
export const createTooltipConfig = (
    isDark: boolean,
    textColors: TextColors,
): TooltipConfig => ({
    backgroundColor: isDark
        ? 'rgba(0, 0, 0, 0.85)'
        : 'rgba(255, 255, 255, 0.95)',
    bodyColor: textColors.emphasized,
    bodyFont: {
        size: 12,
    },
    borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    borderWidth: 1,
    cornerRadius: 6,
    padding: 10,
    titleColor: textColors.emphasized,
    titleFont: {
        size: 13,
    },
});
